export type AudioWaveformBarAlignment = "center" | "bottom" | "top";

export interface WaveformBarRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export function resolveWaveformBarRect(
	normalizedHeight: number,
	index: number,
	options: {
		waveHeightPx: number;
		barSlotWidth?: number;
		barWidth?: number;
		barPadding?: number;
		alignment?: AudioWaveformBarAlignment;
	},
): WaveformBarRect {
	const {
		waveHeightPx,
		barSlotWidth = 10,
		barWidth = 6,
		barPadding = 2,
		alignment = "center",
	} = options;

	const height = normalizedHeight * (waveHeightPx - 4);

	let y: number;
	switch (alignment) {
		case "bottom":
			y = waveHeightPx - height;
			break;
		case "top":
			y = 0;
			break;
		default:
			y = (waveHeightPx - height) / 2;
			break;
	}

	return {
		x: index * barSlotWidth + barPadding,
		y,
		width: barWidth,
		height,
	};
}

/** Max vertical stretch vs final bar height during bounce peak. */
export const REVEALED_BAR_STRETCH_OVERSHOOT_MAX = 1.52;

/** Max horizontal stretch vs final bar width during bounce peak. */
export const REVEALED_BAR_WIDTH_OVERSHOOT_MAX = 1.2;

/**
 * Dot → full bar (t ≤ 1), then overshoot as a multiplier on the final dimension (t > 1).
 * Previously overshoot was applied to the dot→full delta, so t=1.05 only added ~5% to height.
 */
function resolveStretchDimension(
	dotSize: number,
	fullSize: number,
	t: number,
	maxT: number,
): number {
	const clamped = Math.max(0, Math.min(t, maxT));
	if (clamped <= 1) {
		return dotSize + (fullSize - dotSize) * clamped;
	}
	return fullSize * clamped;
}

/**
 * Fast start → single elastic overshoot → smooth ease-out settle.
 * Returns values > 1 mid-curve; rect layout clamps via overshoot max constants.
 */
export function easeBufferBarStretchReveal(t: number): number {
	if (t <= 0) {
		return 0;
	}
	if (t >= 1) {
		return 1;
	}

	// Fast initial rise (ease-out cubic — steep velocity from the dot).
	const rise = 1 - (1 - t) ** 3;

	// One elastic bump that decays smoothly toward the end (no abrupt snap-back).
	const overshootScale = 1.03;
	const wave = Math.sin(t * Math.PI * 1.02);
	const decay = (1 - t) ** 0.72;

	return rise + overshootScale * wave * decay;
}

/** @deprecated Use easeBufferBarStretchReveal. */
export function easeOutBackReveal(t: number): number {
	return easeBufferBarStretchReveal(t);
}

/** Interpolate from a centred dot to the final bar rect as buffer reveal progresses. */
export function resolveRevealedBarRect(
	normalizedHeight: number,
	index: number,
	revealProgress: number,
	options: {
		waveHeightPx: number;
		barSlotWidth?: number;
		barWidth?: number;
		barPadding?: number;
		alignment?: AudioWaveformBarAlignment;
	},
): WaveformBarRect {
	const full = resolveWaveformBarRect(normalizedHeight, index, options);
	const dotSize = Math.max(2, Math.min(full.width, full.height * 0.15, 4));
	const heightT = Math.max(
		0,
		Math.min(revealProgress, REVEALED_BAR_STRETCH_OVERSHOOT_MAX),
	);
	const widthT = Math.max(
		0,
		Math.min(revealProgress, REVEALED_BAR_WIDTH_OVERSHOOT_MAX),
	);
	const width = resolveStretchDimension(
		dotSize,
		full.width,
		widthT,
		REVEALED_BAR_WIDTH_OVERSHOOT_MAX,
	);
	const height = resolveStretchDimension(
		dotSize,
		full.height,
		heightT,
		REVEALED_BAR_STRETCH_OVERSHOOT_MAX,
	);
	const x = full.x + (full.width - width) / 2;

	const { alignment = "center" } = options;
	let y: number;
	switch (alignment) {
		case "bottom":
			y = full.y + (full.height - height);
			break;
		case "top":
			y = full.y;
			break;
		default:
			y = full.y + (full.height - height) / 2;
			break;
	}

	return { x, y, width, height };
}

/** Uniform scale from the bar anchor — reliable SVG stretch vs mutating height/width. */
export function resolveBarRevealScale(revealProgress: number): number {
	const t = Math.max(0, Math.min(1, revealProgress));
	const minScale = 4 / 6;
	return minScale + (1 - minScale) * t;
}

export function resolveBarRevealTransform(
	rect: WaveformBarRect,
	revealProgress: number,
	alignment: AudioWaveformBarAlignment,
	waveHeightPx: number,
): string | undefined {
	if (revealProgress >= 1) {
		return undefined;
	}

	const scale = resolveBarRevealScale(revealProgress);
	const centerX = rect.x + rect.width / 2;
	let originY: number;
	switch (alignment) {
		case "bottom":
			originY = waveHeightPx;
			break;
		case "top":
			originY = 0;
			break;
		default:
			originY = waveHeightPx / 2;
			break;
	}

	return `translate(${centerX} ${originY}) scale(${scale}) translate(${-centerX} ${-originY})`;
}
